import { Box, Button, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { ReactComponent as DesktopIcon } from 'assets/icons/desktop.svg';
import { ReactComponent as MobileIcon } from 'assets/icons/mobile.svg';
import { useAppSelector } from 'modules/Editor/store';
import { selectInfographHeightPx, selectPageBackgroundString } from 'modules/Editor/store/infographSelector';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { ResponsivePageView } from 'modules/ResponsiveLinkView/ResponsiveLinkView';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DESKTOP_WINDOW_SIZE, MOBILE_WINDOW_SIZE } from './ResponsiveLinkPreview.config';
import { getPreviewScale } from './ResponsiveLinkPreview.helpers';

type ResponsivePreviewContentProps = {
  displayRef: React.RefObject<HTMLDivElement>;
  customShouldResize: () => boolean;
  onClose: () => void;
};

export const ResponsivePreviewContent = ({
  displayRef,
  customShouldResize,
  onClose,
}: ResponsivePreviewContentProps) => {
  const [previewSize, setPreviewSize] = useState(DESKTOP_WINDOW_SIZE);
  const activePageId = useAppSelector(selectActivePage);
  const pageBackground = useAppSelector(selectPageBackgroundString(activePageId));
  const heightPx = useAppSelector(selectInfographHeightPx);
  const scaledContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('common_navbar');

  const { scaledHeight, fullScreenScale } = getPreviewScale();
  const scaleContainerStyle = {
    transform: `scale(${fullScreenScale})`,
    transformOrigin: 'center',
    width: previewSize.width,
    height: previewSize.height,
  };

  const onClickMobile = () => setPreviewSize(MOBILE_WINDOW_SIZE);
  const onClickDesktop = () => setPreviewSize(DESKTOP_WINDOW_SIZE);

  const customDistanceLeft = useCallback(
    (element: HTMLElement) => {
      const designRect = element.getBoundingClientRect();
      if (!scaledContainerRef.current) return 0;

      // for full page sized widgets for desktop
      // calculate the scaled distance from the left to the "screen" left
      const scaledRect = scaledContainerRef.current.getBoundingClientRect();
      const scaledDistance = designRect.left - scaledRect.left;
      const distanceToScreenLeft = scaledDistance / fullScreenScale;

      return distanceToScreenLeft;
    },
    [fullScreenScale],
  );

  const isMobile = previewSize.width === MOBILE_WINDOW_SIZE.width;
  const isDesktop = previewSize.width === DESKTOP_WINDOW_SIZE.width;

  const mobileLabel = t('modals.share-panel.responsive-share.mobile');
  const desktopLabel = t('modals.share-panel.responsive-share.desktop');
  const closeLabel = t('modals.share-panel.responsive-share.close');

  return (
    <>
      <Flex align='center' gap={2} p={2} mb={5} bg='white'>
        <Button onClick={onClose} variant='ghost' size='sm' justifySelf='flex-start'>
          {closeLabel}
        </Button>
        <Flex w='full' justify='center' gap={1}>
          <Tooltip hasArrow placement='bottom' label={mobileLabel}>
            <IconButton
              variant='icon-btn-toolbar'
              aria-label={mobileLabel}
              icon={<MobileIcon />}
              onClick={onClickMobile}
              isActive={isMobile}
            />
          </Tooltip>
          <Tooltip hasArrow placement='bottom' label={desktopLabel}>
            <IconButton
              variant='icon-btn-toolbar'
              aria-label={desktopLabel}
              icon={<DesktopIcon />}
              onClick={onClickDesktop}
              isActive={isDesktop}
            />
          </Tooltip>
        </Flex>
      </Flex>
      <Flex justify='center' align='center' height={`${scaledHeight}px`}>
        <Box style={scaleContainerStyle} ref={scaledContainerRef}>
          <PreviewNavbar fullScreenScale={fullScreenScale} isMobile={isMobile} />
          <Box
            bg={pageBackground}
            overflowY='scroll'
            maxHeight='100%'
            p={0}
            w={previewSize.width + 'px'}
            ref={displayRef}
            borderBottomRadius={isMobile ? 'xl' : ''}
          >
            <ResponsivePageView
              pageContainerOverflow='hidden'
              windowSize={{ width: previewSize.width, height: previewSize.height }}
              customDistanceLeft={customDistanceLeft}
              customShouldResize={customShouldResize}
              minPageHeight={previewSize.height + 'px'}
              pageHeight={isDesktop && previewSize.height > heightPx ? previewSize.height + 'px' : undefined}
            />
          </Box>
        </Box>
      </Flex>
    </>
  );
};

interface PreviewNavbarProps {
  fullScreenScale: number;
  isMobile: boolean;
}

const CIRCLE_SIZE = 10;
const HEIGHT = { mobile: 35, desktop: 27 };
const GAP = 8;
export const PreviewNavbar = ({ fullScreenScale, isMobile }: PreviewNavbarProps) => {
  const scaledCircle = CIRCLE_SIZE / fullScreenScale;
  const height = isMobile ? HEIGHT.mobile : HEIGHT.desktop;
  const scaledHeight = height / fullScreenScale;
  const scaledGap = GAP / fullScreenScale;

  return (
    <Flex
      align={isMobile ? 'end' : 'center'}
      borderTopRadius={isMobile ? '30px' : 'xl'}
      px={scaledGap + 'px'}
      gap={scaledGap + 'px'}
      w='full'
      bg='gray.50'
      height={scaledHeight + 'px'}
    >
      {isMobile ? null : (
        <>
          <Box w={scaledCircle + 'px'} h={scaledCircle + 'px'} borderRadius='50%' bg='#F3605C' />
          <Box w={scaledCircle + 'px'} h={scaledCircle + 'px'} borderRadius='50%' bg='#F8BE39' />
          <Box w={scaledCircle + 'px'} h={scaledCircle + 'px'} borderRadius='50%' bg='#50C845' />
        </>
      )}
    </Flex>
  );
};

export default ResponsivePreviewContent;
